import { useEffect, useRef, useState } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'

/**
 * Connection states for SSE stream
 */
export type SSEConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error'

/**
 * Options for SSE stream hook
 */
interface UseSSEStreamOptions {
  /** API endpoint URL for the SSE stream */
  endpoint: string
  /** Access token for authentication */
  accessToken: string
  /** Callback for handling events - receives event type and parsed data */
  onEvent: (eventType: string, data: any) => void
  /** Optional error handler */
  onError?: (error: any) => void
  /** Whether the stream is enabled (default: true) */
  enabled?: boolean
  /** Event types to listen for (if not specified, listens to common ones) */
  eventTypes?: string[]
}

/**
 * Custom hook for Server-Sent Events (SSE) streaming
 *
 * Uses @microsoft/fetch-event-source to support custom headers (Authorization)
 * Manages connection lifecycle, handles authentication, and provides connection state tracking.
 *
 * @example
 * ```tsx
 * const { isConnected, connectionState } = useSSEStream({
 *   endpoint: '/api/v1/call-logs/stream',
 *   accessToken: token,
 *   onEvent: (type, data) => console.log(type, data),
 *   enabled: !!token
 * })
 * ```
 */
export function useSSEStream({
  endpoint,
  accessToken,
  onEvent,
  onError,
  enabled = true,
  eventTypes = [
    'call_logs.created',
    'call_logs.updated',
    'transcripts.created',
    'transcripts.updated'
  ]
}: UseSSEStreamOptions) {
  const [connectionState, setConnectionState] = useState<SSEConnectionState>('disconnected')
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastEventTimestamp = useRef<number>(Date.now())

  useEffect(() => {
    // Don't connect if disabled or missing token
    if (!enabled || !accessToken) {
      setConnectionState('disconnected')
      return
    }

    setConnectionState('connecting')

    // Create abort controller for cleanup
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    // Start SSE connection with custom headers
    fetchEventSource(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      signal: abortController.signal,

      async onopen(response) {
        if (response.ok) {
          setConnectionState('connected')
          lastEventTimestamp.current = Date.now()
        } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          // Client error (4xx) - don't retry
          console.error('SSE connection failed with status:', response.status)
          const errorText = await response.text()
          console.error('Error details:', errorText)
          setConnectionState('error')
          onError?.(new Error(`HTTP ${response.status}: ${errorText}`))
          throw new Error(`HTTP ${response.status}`) // Stop retrying
        } else {
          // Server error (5xx) or rate limit - will retry
          console.warn('SSE connection failed, will retry:', response.status)
          setConnectionState('error')
        }
      },

      onmessage(event) {
        // Skip empty messages and comments
        if (!event.data || event.data.trim() === '' || event.data.startsWith(':')) {
          return
        }

        try {
          // Parse the message data
          const parsedData = JSON.parse(event.data)

          // Handle both formats:
          // 1. event field set (standard SSE): event="transcripts.updated" data={...}
          // 2. event_type in data (custom format): data={"event_type":"transcripts.updated","data":{...}}
          const eventType = event.event || parsedData.event_type || parsedData.type
          const eventData = parsedData.data !== undefined ? parsedData.data : parsedData

          if (eventType && eventTypes.includes(eventType)) {
            lastEventTimestamp.current = Date.now()
            onEvent(eventType, eventData)
          } else if (eventType) {
            console.warn('SSE event not in eventTypes:', eventType, 'eventTypes:', eventTypes)
          }
        } catch (error) {
          console.error('Failed to parse SSE message:', error)
          console.error('Raw data was:', event.data)
        }
      },

      onerror(error) {
        console.error('SSE connection error:', error)
        setConnectionState('error')
        onError?.(error)

        // Don't throw - allow automatic retry unless it's an abort
        if (abortController.signal.aborted) {
          throw error // Stop if manually aborted
        }
      },

      onclose() {
        setConnectionState('disconnected')
      },
    }).catch((error) => {
      // Only log if it's not an intentional abort
      if (!abortController.signal.aborted) {
        console.error('SSE connection terminated:', error)
        setConnectionState('error')
      }
    })

    // Cleanup on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
        setConnectionState('disconnected')
      }
    }
  }, [endpoint, accessToken, enabled, onEvent, onError])

  // Manual disconnect function
  const disconnect = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setConnectionState('disconnected')
    }
  }

  return {
    /** Current connection state */
    connectionState,
    /** Whether currently connected */
    isConnected: connectionState === 'connected',
    /** Manually disconnect the stream */
    disconnect,
    /** Timestamp of last received event */
    lastEventTimestamp: lastEventTimestamp.current
  }
}
